import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'

export const useMasterDataStore = defineStore('masterData', () => {
  const districts = ref([])
  const vehicles = ref([])
  const colours = ref([])
  const services = ref([])
  const animals = ref([])
  const staff = ref([])
  const doctors = ref([])
  const surgeons = ref([])
  const medicines = ref([])
  const statuses = ref([])
  const postSurgeryLocations = ref([])
  const loading = ref(false)
  const error = ref(null)

  // District operations
  const getDistricts = async () => {
    try {
      const response = await api.get('/masters/districts?per_page=100')
      console.log('Raw API response:', response)
      console.log('response.data:', response.data)
      console.log('typeof response.data:', typeof response.data)
      districts.value = response.data?.data || response.data || []
      console.log('districts.value set to:', districts.value)
      return districts.value
    } catch (err) {
      console.error('Error loading districts:', err)
      error.value = 'Failed to load districts'
      districts.value = []
    }
  }

  const createDistrict = async (data) => {
    try {
      const response = await api.post('/masters/districts', data)
      districts.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create district'
      throw err
    }
  }

  const updateDistrict = async (id, data) => {
    try {
      const response = await api.put(`/masters/districts/${id}`, data)
      const index = districts.value.findIndex(d => d.id === id)
      if (index >= 0) districts.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update district'
      throw err
    }
  }

  const deleteDistrict = async (id) => {
    try {
      await api.delete(`/masters/districts/${id}`)
      districts.value = districts.value.filter(d => String(d.id) !== String(id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete district'
      throw err
    }
  }

  // Vehicle operations
  const getVehicles = async (districtId = null) => {
    try {
      const params = districtId ? { district_id: districtId, per_page: 100 } : { per_page: 100 }
      const response = await api.get('/masters/vehicles', { params })
      vehicles.value = response.data?.data || response.data || []
      return vehicles.value
    } catch (err) {
      error.value = 'Failed to load vehicles'
      vehicles.value = []
    }
  }

  const createVehicle = async (data) => {
    try {
      const response = await api.post('/masters/vehicles', data)
      vehicles.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create vehicle'
      throw err
    }
  }

  const updateVehicle = async (id, data) => {
    try {
      const response = await api.put(`/masters/vehicles/${id}`, data)
      const index = vehicles.value.findIndex(v => v.id === id)
      if (index >= 0) vehicles.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update vehicle'
      throw err
    }
  }

  const deleteVehicle = async (id) => {
    try {
      await api.delete(`/masters/vehicles/${id}`)
      vehicles.value = vehicles.value.filter(v => v.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete vehicle'
    }
  }

  // Colour operations
  const getColours = async () => {
    try {
      const response = await api.get('/masters/colours?per_page=100')
      colours.value = response.data?.data || response.data || []
      return colours.value
    } catch (err) {
      error.value = 'Failed to load colours'
      colours.value = []
    }
  }

  const createColour = async (data) => {
    try {
      const response = await api.post('/masters/colours', data)
      colours.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create colour'
      throw err
    }
  }

  const updateColour = async (id, data) => {
    try {
      const response = await api.put(`/masters/colours/${id}`, data)
      const index = colours.value.findIndex(c => c.id === id)
      if (index >= 0) colours.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update colour'
      throw err
    }
  }

  const deleteColour = async (id) => {
    try {
      await api.delete(`/masters/colours/${id}`)
      colours.value = colours.value.filter(c => c.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete colour'
    }
  }

  // Service operations
  const getServices = async () => {
    try {
      const response = await api.get('/masters/services?per_page=100')
      services.value = response.data?.data || response.data || []
      return services.value
    } catch (err) {
      error.value = 'Failed to load services'
      services.value = []
    }
  }

  const createService = async (data) => {
    try {
      const response = await api.post('/masters/services', data)
      services.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create service'
      throw err
    }
  }

  const updateService = async (id, data) => {
    try {
      const response = await api.put(`/masters/services/${id}`, data)
      const index = services.value.findIndex(s => s.id === id)
      if (index >= 0) services.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update service'
      throw err
    }
  }

  const deleteService = async (id) => {
    try {
      await api.delete(`/masters/services/${id}`)
      services.value = services.value.filter(s => s.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete service'
    }
  }

  // Animal operations
  const getAnimals = async () => {
    try {
      const response = await api.get('/masters/animals?per_page=100')
      animals.value = response.data?.data || response.data || []
      return animals.value
    } catch (err) {
      error.value = 'Failed to load animals'
      animals.value = []
    }
  }

  const createAnimal = async (data) => {
    try {
      const response = await api.post('/masters/animals', data)
      animals.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create animal'
      throw err
    }
  }

  const updateAnimal = async (id, data) => {
    try {
      const response = await api.put(`/masters/animals/${id}`, data)
      const index = animals.value.findIndex(a => a.id === id)
      if (index >= 0) animals.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update animal'
      throw err
    }
  }

  const deleteAnimal = async (id) => {
    try {
      await api.delete(`/masters/animals/${id}`)
      animals.value = animals.value.filter(a => a.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete animal'
    }
  }

  // Staff operations
  const getStaff = async (districtId = null) => {
    try {
      const params = districtId ? { district_id: districtId, per_page: 100 } : { per_page: 100 }
      const response = await api.get('/masters/staff', { params })
      staff.value = response.data?.data || response.data || []
      return staff.value
    } catch (err) {
      error.value = 'Failed to load staff'
      staff.value = []
    }
  }

  const createStaff = async (data) => {
    try {
      const response = await api.post('/masters/staff', data)
      staff.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create staff'
      throw err
    }
  }

  const updateStaff = async (id, data) => {
    try {
      const response = await api.put(`/masters/staff/${id}`, data)
      const index = staff.value.findIndex(s => s.id === id)
      if (index >= 0) staff.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update staff'
      throw err
    }
  }

  const deleteStaff = async (id) => {
    try {
      await api.delete(`/masters/staff/${id}`)
      staff.value = staff.value.filter(s => s.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete staff'
    }
  }

  // Doctor operations
  const getDoctors = async (districtId = null) => {
    try {
      const params = districtId ? { district_id: districtId, per_page: 100 } : { per_page: 100 }
      const response = await api.get('/masters/doctors', { params })
      doctors.value = response.data?.data || response.data || []
      return doctors.value
    } catch (err) {
      error.value = 'Failed to load doctors'
      doctors.value = []
    }
  }

  const createDoctor = async (data) => {
    try {
      const response = await api.post('/masters/doctors', data)
      doctors.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create doctor'
      throw err
    }
  }

  const updateDoctor = async (id, data) => {
    try {
      const response = await api.put(`/masters/doctors/${id}`, data)
      const index = doctors.value.findIndex(d => d.id === id)
      if (index >= 0) doctors.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update doctor'
      throw err
    }
  }

  const deleteDoctor = async (id) => {
    try {
      await api.delete(`/masters/doctors/${id}`)
      doctors.value = doctors.value.filter(d => d.id !== id)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete doctor'
    }
  }

  // Surgeon operations
  const getSurgeons = async () => {
    try {
      const response = await api.get('/masters/surgeons?per_page=100')
      surgeons.value = response.data?.data || response.data || []
      return surgeons.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load surgeons'
      surgeons.value = []
      throw err
    }
  }

  // Status operations
  const getStatuses = async () => {
    try {
      const response = await api.get('/masters/statuses?per_page=100')
      statuses.value = response.data?.data || response.data || []
      return statuses.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load statuses'
      statuses.value = []
      throw err
    }
  }

  const createStatus = async (data) => {
    try {
      const response = await api.post('/masters/statuses', data)
      statuses.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create status'
      throw err
    }
  }

  const updateStatus = async (id, data) => {
    try {
      const response = await api.put(`/masters/statuses/${id}`, data)
      const index = statuses.value.findIndex(status => String(status.id) === String(id))
      if (index >= 0) statuses.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update status'
      throw err
    }
  }

  const deleteStatus = async (id) => {
    try {
      await api.delete(`/masters/statuses/${id}`)
      statuses.value = statuses.value.filter(status => String(status.id) !== String(id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete status'
      throw err
    }
  }

  // Post surgery location operations
  const getPostSurgeryLocations = async () => {
    try {
      const response = await api.get('/masters/post-surgery-locations?per_page=100')
      postSurgeryLocations.value = response.data?.data || response.data || []
      return postSurgeryLocations.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load post surgery locations'
      postSurgeryLocations.value = []
      throw err
    }
  }

  const createPostSurgeryLocation = async (data) => {
    try {
      const response = await api.post('/masters/post-surgery-locations', data)
      postSurgeryLocations.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create post surgery location'
      throw err
    }
  }

  const updatePostSurgeryLocation = async (id, data) => {
    try {
      const response = await api.put(`/masters/post-surgery-locations/${id}`, data)
      const index = postSurgeryLocations.value.findIndex(location => String(location.id) === String(id))
      if (index >= 0) postSurgeryLocations.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update post surgery location'
      throw err
    }
  }

  const deletePostSurgeryLocation = async (id) => {
    try {
      await api.delete(`/masters/post-surgery-locations/${id}`)
      postSurgeryLocations.value = postSurgeryLocations.value.filter(location => String(location.id) !== String(id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete post surgery location'
      throw err
    }
  }

  const createSurgeon = async (data) => {
    try {
      const response = await api.post('/masters/surgeons', data)
      surgeons.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create surgeon'
      throw err
    }
  }

  const updateSurgeon = async (id, data) => {
    try {
      const response = await api.put(`/masters/surgeons/${id}`, data)
      const index = surgeons.value.findIndex(s => s.id === id)
      if (index >= 0) surgeons.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update surgeon'
      throw err
    }
  }

  const deleteSurgeon = async (id) => {
    try {
      await api.delete(`/masters/surgeons/${id}`)
      surgeons.value = surgeons.value.filter(s => String(s.id) !== String(id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete surgeon'
      throw err
    }
  }

  // Medicine operations
  const getMedicines = async () => {
    try {
      const response = await api.get('/masters/medicines?per_page=100')
      medicines.value = response.data?.data || response.data || []
      return medicines.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load medicines'
      medicines.value = []
      throw err
    }
  }

  const createMedicine = async (data) => {
    try {
      const response = await api.post('/masters/medicines', data)
      medicines.value.push(response.data?.data || data)
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create medicine'
      throw err
    }
  }

  const updateMedicine = async (id, data) => {
    try {
      const response = await api.put(`/masters/medicines/${id}`, data)
      const index = medicines.value.findIndex(m => m.id === id)
      if (index >= 0) medicines.value[index] = response.data?.data || data
      return response.data?.data || data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update medicine'
      throw err
    }
  }

  const deleteMedicine = async (id) => {
    try {
      await api.delete(`/masters/medicines/${id}`)
      medicines.value = medicines.value.filter(m => String(m.id) !== String(id))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete medicine'
      throw err
    }
  }

  // Load all master data
  const loadAll = async (districtId = null) => {
    loading.value = true
    try {
      await Promise.all([
        getDistricts(),
        getColours(),
        getServices(),
        getAnimals(),
        getVehicles(districtId),
        getStaff(districtId),
        getDoctors(districtId),
        getStatuses(),
      ])
    } finally {
      loading.value = false
    }
  }

  return {
    districts,
    vehicles,
    colours,
    services,
    animals,
    staff,
    doctors,
    surgeons,
    medicines,
    statuses,
    postSurgeryLocations,
    loading,
    error,
    getDistricts,
    createDistrict,
    updateDistrict,
    deleteDistrict,
    getVehicles,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    getColours,
    createColour,
    updateColour,
    deleteColour,
    getStatuses,
    createStatus,
    updateStatus,
    deleteStatus,
    getPostSurgeryLocations,
    createPostSurgeryLocation,
    updatePostSurgeryLocation,
    deletePostSurgeryLocation,
    getServices,
    createService,
    updateService,
    deleteService,
    getAnimals,
    createAnimal,
    updateAnimal,
    deleteAnimal,
    getStaff,
    createStaff,
    updateStaff,
    deleteStaff,
    getDoctors,
    createDoctor,
    updateDoctor,
    deleteDoctor,
    getSurgeons,
    createSurgeon,
    updateSurgeon,
    deleteSurgeon,
    getMedicines,
    createMedicine,
    updateMedicine,
    deleteMedicine,
    loadAll,
  }
})
